#!/usr/bin/env node
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import type { TransactionReceipt } from "@saberhq/solana-contrib";
import { PublicKey } from "@saberhq/solana-contrib";
import { TOKEN_PROGRAM_ID } from "@saberhq/token-utils";
import BN from "bn.js";
import { Command } from "commander";
import { printTable } from "console-table-printer";
import { exit } from "process";
import invariant from "tiny-invariant";

import * as pack from "../package.json";
import type { QuarryData, TokenSwapAccount } from "../src";
import { findPositionAddress, QUARRY_CODERS, tick2Price } from "../src";
import {
  ConstPriceProvider,
  ConstTokenProvider,
  ExchangeRate,
} from "../src/programs/exchangeRate";
import { PositionWrapper } from "../src/wrappers/positionWrapper";
import { getQuarries } from "./mine";
import { loadTokens } from "./tokenList";
import {
  catchFinallyExit,
  confirmTx,
  getTokenAccountsByOwnerAndMint,
  makeSDK,
  mustGetTokenInfo,
  printObjectJSON,
  printObjectTable,
  receiptLog,
} from "./utils";

export const program = new Command().name("crema-pos-wrapper");
program.version(pack.version);

// New a position wrapper
program
  .command("new")
  .description("Create a CremaSwap's position wrapper")
  .requiredOption("-l, --lower <lower>", "The effective price lower tick")
  .requiredOption("-u, --upper <upper>", "The effective price upper tick")
  .requiredOption("-p, --pair <pair>", "The CremaSwap's swap pair key")
  .action((arg) => {
    const p = new PublicKey(arg.pair);
    const l: number = arg.lower;
    const u: number = arg.upper;
    catchFinallyExit(newPositionWrapper(p, l, u));
  });

// Update position wrapper's effective price tick range.
program
  .command("update")
  .description("Update position wrapper's effective price tick range")
  .argument("<wrapper>", "The position wrapper")
  .requiredOption("-u, --upper <upper>", "The effective price upper tick")
  .requiredOption("-l, --lower <lower>", "The effective price lower tick")
  .action((wrapper: any, arg: any) => {
    const w = new PublicKey(wrapper);
    const u: number = arg.upper;
    const l: number = arg.lower;
    catchFinallyExit(updatePositionWrapper(w, l, u));
  });

// Transfer position wrapper's admin
program
  .command("transfer-admin")
  .description("Transfer position wrapper's admin")
  .argument("<wrapper>", "The position wrapper")
  .requiredOption("-a, --admin <admin>", "The position wrapper's next admin")
  .action((wrapper: any, arg: any) => {
    const w = new PublicKey(wrapper);
    const a = new PublicKey(arg.admin);
    catchFinallyExit(transferAdmin(w, a));
  });

// Accept position wrapper's new admin
program
  .command("accept-admin")
  .description("Accept position wrapper's new admin")
  .argument("<wrapper>", "The position wrapper's next admin")
  .action((wrapper: string) => {
    const w = new PublicKey(wrapper);
    catchFinallyExit(acceptAdmin(w));
  });

// Mint wrap tokens.
program
  .command("mint")
  .description("Stake CremaSwap's position and mint wrap tokens")
  .requiredOption("-w --wrapper <wrapper>", "The position wrapper")
  .argument("<nftMint>", "The swap position's nft mint address")
  .action((nftMint: any, arg: any) => {
    const w = new PublicKey(arg.wrapper);
    const m = new PublicKey(nftMint);
    catchFinallyExit(mint(w, m));
  });

// Mint wrap tokens.
program
  .command("mint-stake")
  .description("Stake CremaSwap's position and mint wrap tokens")
  .requiredOption("-w --wrapper <wrapper>", "The position wrapper")
  .requiredOption("-r --rewarder <rewarder>", "The quarry mine's rewarder key")
  .argument("<nftMint>", "The swap position's nft mint address")
  .action((nftMint: any, arg: any) => {
    const w = new PublicKey(arg.wrapper);
    const m = new PublicKey(nftMint);
    const r = new PublicKey(arg.rewarder);
    catchFinallyExit(mintAndStake(w, m, r));
  });

// Mint wrap tokens.
program
  .command("unstake-burn")
  .description("Stake CremaSwap's position and mint wrap tokens")
  .requiredOption("-w --wrapper <wrapper>", "The position wrapper")
  .requiredOption("-r --rewarder <rewarder>", "The quarry mine's rewarder key")
  .option("-c --claim", "If claim the rewards", false)
  .argument("<nftMint>", "The swap position's nft mint address")
  .action((nftMint: any, arg: any) => {
    const w = new PublicKey(arg.wrapper);
    const m = new PublicKey(nftMint);
    const r = new PublicKey(arg.rewarder);
    const c = arg.claim;
    catchFinallyExit(unstakeAndBurn(w, m, r, c));
  });

// Get position wrapper's information
program
  .command("info")
  .description("Get position wrapper information")
  .argument("<wrapper>", "The position wrapper")
  .option("-r --rewarder <rewarder>", "The quarry mine's rewarder key")
  .option("-d --detail [detail]", "Show the detail[tvl,liquity...] of wrapper")
  .action((wrapper: string, arg: any) => {
    const w = new PublicKey(wrapper);
    const r = new PublicKey(arg.rewarder);
    if (arg.detail) {
      catchFinallyExit(fetchWrapperWithDetail(w, r));
    } else {
      catchFinallyExit(fetchPositionWrapper(w));
    }
  });

// Burn wrap tokens.
program
  .command("burn")
  .description("Unstake CremaSwap's position and burn wrap tokens")
  .requiredOption("-w --wrapper <wrapper>", "The position wrapper")
  .argument("<nftMint>", "The swap position's nft mint address")
  .action((nftMint: string, arg: any) => {
    const w = new PublicKey(arg.wrapper);
    const m = new PublicKey(nftMint);
    catchFinallyExit(burn(w, m));
  });

// Get the stake position information
program
  .command("stake-position-info")
  .description("Get staked position information")
  .argument("<nftMint>", "The swap position's nft mint address")
  .action((nftMint: string) => {
    const m = new PublicKey(nftMint);
    catchFinallyExit(fetchStakePosition(m));
  });

// Get the stake positions
program
  .command("stake-positions")
  .description("Get staked position list")
  .option("-w --wrapper <wrapper>", "The position wrapper")
  .option("-o --owner [owner]", "The owner of stake position")
  .option("-e --empty", "If the position is not holding by wrapper")
  .action((arg) => {
    const w = arg.wrapper !== undefined ? new PublicKey(arg.wrapper) : null;
    catchFinallyExit(fetchStakePositions(w, arg.owner, !arg.empty));
  });

// Calculate the wrap token amount.
program
  .command("wrap-amount")
  .description("Calculate the wrap token amount if stake to position wrapper")
  .requiredOption("-w --wrapper <wrapper>", "The position wrapper")
  .argument("<nftMint>", "The swap position's nft mint address")
  .action((nftMint: any, arg: any) => {
    const w = new PublicKey(arg.wrapper);
    const m = new PublicKey(nftMint);
    catchFinallyExit(calculateWrapAmount(w, m));
  });

// Get all wrappers info
program
  .command("wrappers")
  .description("Get wrappers info")
  .option("-d --detail [detail]", "Show the detail[tvl,liquity...] of wrapper")
  .action((arg) => {
    if (arg.detail === true) {
      catchFinallyExit(fetchWrappersWithDetail());
    } else {
      catchFinallyExit(fetchWrappers());
    }
  });

program
  .command("rate")
  .argument("<base>", "The base token")
  .argument("<quote>", "The quote token")
  .option("-a --address, [address]", "is address?")
  .action((base: string, quote: string, arg: any) => {
    const er = new ExchangeRate(
      new ConstPriceProvider(),
      new ConstTokenProvider()
    );
    er.load()
      .then(() => {
        if (arg.address) {
          const baseAddress = new PublicKey(base);
          const quoteAddress = new PublicKey(quote);
          const price = er.price(baseAddress, quoteAddress);
          printObjectTable({
            base: base,
            quote: quote,
            baseSymbol: er.tokenInfo(baseAddress)?.symbol,
            quoteSymbol: er.tokenInfo(quoteAddress)?.symbol,
            price: price,
          });
        } else {
          const price = er.price(base, quote);
          printObjectTable({ base: base, quote: quote, price: price });
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

async function newPositionWrapper(
  pair: PublicKey,
  lowerTick: number,
  upperTick: number
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const { tx } = await sdk.positionWrapper.newWrapper({
    etrMin: lowerTick,
    etrMax: upperTick,
    swap: pair,
  });
  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function fetchPositionWrapper(wrapper: PublicKey) {
  const sdk = makeSDK();
  const info = await PositionWrapper.fetchPositionWrapper(
    wrapper,
    sdk.provider.connection
  );
  if (info === null) {
    console.log("PositionWrapper %s not found", wrapper.toBase58());
    return;
  }
  printObjectTable(info);
}

async function updatePositionWrapper(
  wrapper: PublicKey,
  etrMin: number,
  etrMax: number
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const tx = sdk.positionWrapper.updateWrapper(
    wrapper,
    etrMin,
    etrMax,
    sdk.provider.wallet.publicKey
  );
  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function transferAdmin(
  wrapper: PublicKey,
  nextAdmin: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const tx = sdk.positionWrapper.transferAdmin(wrapper, nextAdmin);
  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function acceptAdmin(wrapper: PublicKey): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const tx = sdk.positionWrapper.acceptAdmin(wrapper);
  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function mint(
  wrapper: PublicKey,
  nftMint: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();

  const wrapperInfo = await PositionWrapper.fetchPositionWrapper(
    wrapper,
    sdk.provider.connection
  );
  invariant(wrapperInfo !== null, "wrapper not found");

  const userSwapPosition = await PositionWrapper.fetchSwapPositionByOwner(
    wrapperInfo.swapKey,
    nftMint,
    sdk.provider.wallet.publicKey,
    sdk.provider.connection
  );
  invariant(userSwapPosition !== null, "Can't find the swap position you own");

  const res = await sdk.positionWrapper.mintWrapTokens({
    wrapper: wrapperInfo,
    positionInfo: userSwapPosition,
    tokenProgram: TOKEN_PROGRAM_ID,
  });

  const receipt = await confirmTx(res.tx);
  printLog(receipt, {
    swapPosition: {
      ...userSwapPosition,
    },
  });

  return receipt;
}

export async function burn(
  wrapper: PublicKey,
  nftMint: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();

  // Get nft account
  const tokenAccounts = await getTokenAccountsByOwnerAndMint(
    sdk.provider.connection,
    sdk.provider.wallet.publicKey,
    nftMint
  );
  if (tokenAccounts.length <= 0) {
    console.log("The nft token account not foundkj");
  }
  invariant(
    tokenAccounts[0] !== undefined,
    "The nft token accuont is undefined"
  );

  // Get wrapper info
  const wrapperInfo = await PositionWrapper.fetchPositionWrapper(
    wrapper,
    sdk.provider.connection
  );
  invariant(wrapperInfo !== null, "wrapper not found");

  const tx = await sdk.positionWrapper.burnWrapTokens({
    wrapper: wrapperInfo,
    nftMint,
    nftAccount: tokenAccounts[0].address,
  });

  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function mintAndStake(
  wrapper: PublicKey,
  nftMint: PublicKey,
  rewarderKey: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();

  // Get wrapper info
  const wrapperInfo = await PositionWrapper.fetchPositionWrapper(
    wrapper,
    sdk.provider.connection
  );
  invariant(wrapperInfo !== null, "wrapper not found");

  const userSwapPosition = await PositionWrapper.fetchSwapPositionByOwner(
    wrapperInfo.swapKey,
    nftMint,
    sdk.provider.wallet.publicKey,
    sdk.provider.connection
  );
  invariant(userSwapPosition !== null, "Can't find the swap position you own");

  const res = await sdk.positionWrapper.mintAndStake({
    wrapper: wrapperInfo,
    nftMint,
    rewarderKey,
  });

  const receipt = await confirmTx(res.tx);
  printLog(receipt, {
    stakePosition: res.position.toBase58(),
    nftVault: res.nftVault.toBase58(),
    quarry: res.quarryWrapper.toBase58(),
    swapPosition: {
      ...userSwapPosition,
    },
  });

  return receipt;
}

async function unstakeAndBurn(
  wrapper: PublicKey,
  nftMint: PublicKey,
  rewarderKey: PublicKey,
  isClaim = false
): Promise<TransactionReceipt> {
  const sdk = makeSDK();

  // Get wrapper info
  const wrapperInfo = await PositionWrapper.fetchPositionWrapper(
    wrapper,
    sdk.provider.connection
  );
  invariant(wrapperInfo !== null, "wrapper not found");

  const tx = await sdk.positionWrapper.unstakeAndBurn({
    wrapper: wrapperInfo,
    nftMint,
    rewarderKey,
    isClaim,
  });

  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function fetchStakePosition(nftMint: PublicKey) {
  const sdk = makeSDK();

  const [position] = await findPositionAddress(nftMint);

  const positionInfo = await PositionWrapper.fetchStakePosition(
    position,
    sdk.provider.connection
  );
  invariant(positionInfo !== null, "stake position not found");
  if (positionInfo === null) {
    console.log("The stake position:%s not found", position.toBase58());
    return;
  }
  printObjectTable(positionInfo);
}

/*eslint-disable  @typescript-eslint/no-explicit-any*/
async function fetchStakePositions(
  wrapper: PublicKey | null,
  owner: any,
  hold: boolean
) {
  const sdk = makeSDK();
  let authority: PublicKey | null;
  if (typeof owner === "boolean") {
    authority = sdk.provider.wallet.publicKey;
  } else if (owner === undefined) {
    authority = null;
  } else {
    authority = new PublicKey(owner);
  }
  const positions = await PositionWrapper.fetchStakePositions(
    wrapper,
    authority,
    sdk.provider.connection,
    hold
  );
  if (positions.length > 0) {
    printTable(positions);
  } else {
    console.log("No position found");
  }
}

async function calculateWrapAmount(wrapper: PublicKey, nftMint: PublicKey) {
  const sdk = makeSDK();
  const wrapperInfo = await PositionWrapper.fetchPositionWrapper(
    wrapper,
    sdk.provider.connection
  );
  invariant(wrapperInfo !== null, "wrapper not found");

  const positionInfo = await PositionWrapper.fetchSwapPositionByNFT(
    wrapperInfo.swapKey,
    nftMint,
    sdk.provider.connection
  );
  invariant(positionInfo !== null, "The swap position not found");

  const amount = PositionWrapper.calculateWrapAmount(wrapperInfo, positionInfo);

  console.log(amount.toString());
}

async function fetchWrappers() {
  const sdk = makeSDK();
  await loadTokens();
  const quarries: Map<string, QuarryData> = new Map();
  (await getQuarries(sdk.provider.connection)).forEach((info) => {
    quarries.set(info.tokenMintKey.toBase58(), info);
  });
  const wrappers = await PositionWrapper.fetchWrappers(sdk.provider.connection);
  if (wrappers.length > 0) {
    const list = [];
    const swapKeys = [];
    const swapMaping: Map<string, TokenSwapAccount> = new Map();
    for (const wrapper of wrappers) {
      swapKeys.push(wrapper.swapKey);
    }
    const swapPairs = await PositionWrapper.fetchCremaSwaps(
      swapKeys,
      sdk.provider.connection
    );
    for (const pair of swapPairs) {
      if (pair === null) {
        continue;
      }
      swapMaping.set(pair.tokenSwapKey.toString(), pair);
    }

    for (const wrapper of wrappers) {
      const quarry = quarries.get(wrapper.wrapMint.toBase58());
      let endTime = "None";
      let weight = 0;
      let rewarder = "None";

      if (quarry !== undefined) {
        if (quarry.famineTs.lt(new BN("100000000000"))) {
          endTime = new Date(
            quarry.famineTs.toNumber() * 1000
          ).toLocaleString();
        }
        weight = quarry.rewardsShare.toNumber();
        rewarder = quarry.rewarder.toBase58();
      }

      const pair = swapMaping.get(wrapper.swapKey.toString());
      invariant(
        pair !== undefined,
        `The pair${wrapper.swapKey.toBase58()} not found`
      );
      list.push({
        pair: `${mustGetTokenInfo(pair.tokenAMint).symbol}-${
          mustGetTokenInfo(pair.tokenBMint).symbol
        }`,
        weight,
        ETR: `[${wrapper.etrMin}, ${wrapper.etrMax}]`,
        EPR: `[${tick2Price(wrapper.etrMin)
          .toDecimalPlaces(6)
          .toString()}, ${tick2Price(wrapper.etrMax)
          .toDecimalPlaces(6)
          .toString()}]`,
        wrapper: wrapper.address.toBase58(),
        swapKey: wrapper.swapKey.toBase58(),
        wrapMint: wrapper.wrapMint.toBase58(),
        rewarder,
        count: wrapper.positionCount,
        endTime,
      });
      swapKeys.push(wrapper.swapKey);
    }
    printTable(list);
  } else {
    console.log("No wrappers found.");
  }
}

async function fetchWrappersWithDetail() {
  const sdk = makeSDK();
  const er = new ExchangeRate(
    new ConstPriceProvider(),
    new ConstTokenProvider()
  );
  await er.load();
  await loadTokens();
  const wrappers = await PositionWrapper.fetchWrappersWithDetail(
    er,
    "USDC",
    sdk.provider.connection
  );
  const swapKeys = [];
  const swapMaping: Map<string, TokenSwapAccount> = new Map();
  for (const wrapper of wrappers) {
    swapKeys.push(wrapper.swapKey);
  }
  const swapPairs = await PositionWrapper.fetchCremaSwaps(
    swapKeys,
    sdk.provider.connection
  );
  for (const pair of swapPairs) {
    if (pair === null) {
      continue;
    }
    swapMaping.set(pair.tokenSwapKey.toString(), pair);
  }
  if (wrappers.length > 0) {
    const list = [];
    for (const wrapper of wrappers) {
      const pair = swapMaping.get(wrapper.swapKey.toString());
      invariant(
        pair !== undefined,
        `The pair${wrapper.swapKey.toBase58()} not found`
      );
      list.push({
        pair: `${mustGetTokenInfo(pair.tokenAMint).symbol}-${
          mustGetTokenInfo(pair.tokenBMint).symbol
        }`,
        wrapper: wrapper.address.toBase58(),
        swapKey: wrapper.swapKey.toBase58(),
        wrapMint: wrapper.wrapMint.toBase58(),
        etrMin: wrapper.etrMin,
        etrMax: wrapper.etrMax,
        count: wrapper.positionCount,
        totalTVL: wrapper.totalTVL.toString(),
        totalAmountA: wrapper.totalAmountA.toDecimalPlaces(0).toString(),
        totalAmountB: wrapper.totalAmountB.toDecimalPlaces(0).toString(),
        effectiveLiquity: wrapper.effectiveLiquity.toString(),
      });
    }
    printTable(list);
  } else {
    console.log("No wrappers found.");
  }
}

async function fetchWrapperWithDetail(
  wrapperKey: PublicKey,
  rewarderKey: PublicKey
) {
  const sdk = makeSDK();
  const er = new ExchangeRate(
    new ConstPriceProvider(),
    new ConstTokenProvider()
  );
  await er.load();
  await loadTokens();
  const wrapper = await PositionWrapper.fetchPositionWrapper(
    wrapperKey,
    sdk.provider.connection
  );
  invariant(wrapper !== null, "wrapper not found");
  const wrapperDetail = await PositionWrapper.fetchWrapperWithDetail(
    wrapper,
    er,
    "USDC",
    sdk.provider.connection
  );
  const swapPair = await PositionWrapper.fetchCremaSwap(
    wrapper.swapKey,
    sdk.provider.connection
  );
  invariant(
    swapPair !== null,
    `The swap pair:${wrapper.swapKey.toBase58()} is null`
  );

  const aprInfo = await PositionWrapper.calculatePositionWrapperAPR(
    rewarderKey,
    wrapper.wrapMint,
    wrapperDetail.totalTVL,
    er,
    "USDC",
    sdk.provider.connection
  );
  printObjectTable({
    pair: `${mustGetTokenInfo(swapPair.tokenAMint).symbol}-${
      mustGetTokenInfo(swapPair.tokenBMint).symbol
    }`,
    ...wrapperDetail,
    ...aprInfo,
  });
}

function printLog(receipt: TransactionReceipt, ext: any = {}) {
  const events: Map<string, any> = new Map();
  QUARRY_CODERS.PositionWrapper.parseProgramLogEvents(
    receipt.response.meta?.logMessages ?? []
  ).forEach((event) => {
    events.set(event.name, event.data);
  });
  const e = Object.fromEntries(events);

  printObjectJSON(
    {
      ...receiptLog(receipt),
      ...ext,
      events: e,
    },
    2
  );
}
